import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  appointmentReportsQueryKey,
  DOCTOR_APPTS_QUERY_KEY,
  downloadReportFile,
  listMyAppointmentsDoctor,
  listReports,
  normalizeAppointmentList,
} from "@/api/appointmentApi";
import { getDoctorProfile } from "@/api/userApi";
import {
  browserTimeZone,
  clinicTimeZoneOf,
  clinicZoneShortLabel,
} from "@/lib/clinicTime";
import { formatSlotStart } from "@/lib/appointmentTimeFormat";
import { PageHeader } from "@/components/common/PageHeader";
import { AppointmentStatusBadge } from "@/components/appointment/AppointmentStatusBadge";
import { DoctorApprovalGate } from "@/components/doctor/DoctorApprovalGate";
import {
  appointmentBucket,
  canJoinCall,
  type AppointmentBucket,
} from "@/lib/appointmentLifecycle";
import type { Appointment } from "@/types/domain";

function DoctorAppointmentFiles({ appointment }: { appointment: Appointment }) {
  const enabled = appointment.status === "CONFIRMED" || appointment.status === "COMPLETED";
  const { data: reports = [], isLoading, isError } = useQuery({
    queryKey: appointmentReportsQueryKey(appointment.id),
    queryFn: () => listReports(appointment.id),
    enabled,
  });

  if (!enabled) return null;

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        Patient files
      </Typography>
      {isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Loading…
        </Typography>
      )}
      {isError && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Files could not be loaded.
        </Typography>
      )}
      {!isLoading && !isError && reports.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          No files shared for this visit.
        </Typography>
      )}
      {reports.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {reports.map((r) => (
            <Box key={r.id}>
              <Button
                size="small"
                variant="text"
                sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 600, p: 0, minHeight: 0 }}
                onClick={() => downloadReportFile(appointment.id, r.id, r.fileName)}
              >
                {r.fileName}
              </Button>
              {r.attachmentNote ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 0.5 }}>
                  {r.attachmentNote}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

const tabs: { label: string; bucket: AppointmentBucket }[] = [
  { label: "Upcoming", bucket: "UPCOMING" },
  { label: "Past", bucket: "PAST" },
  { label: "Cancelled", bucket: "CANCELLED" },
];

export default function DoctorAppointmentsPage() {
  const [tab, setTab] = useState(0);
  const { data: profile } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
  });
  const isApproved = profile?.profileStatus === "APPROVED";

  const { data: raw, isLoading } = useQuery({
    queryKey: DOCTOR_APPTS_QUERY_KEY,
    queryFn: listMyAppointmentsDoctor,
    enabled: isApproved,
  });
  const data = useMemo(() => normalizeAppointmentList(raw), [raw]);

  const filtered = data.filter(
    (a) => appointmentBucket(a) === tabs[tab].bucket,
  );

  if (profile && !isApproved) {
    return (
      <Box>
        <PageHeader
          title="Appointments"
          subtitle="Manage your scheduled, completed and cancelled consultations."
        />
        <DoctorApprovalGate>{null}</DoctorApprovalGate>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Appointments"
        subtitle={`Manage your scheduled, completed and cancelled consultations. Times shown in your zone (${browserTimeZone()}); clinic zone is ${clinicTimeZoneOf(profile?.availabilityTimeZone)} (${clinicZoneShortLabel(profile?.availabilityTimeZone)}).`}
      />

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ px: 1 }}>
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} sx={{ fontWeight: 700 }} />
          ))}
        </Tabs>
      </Paper>

      {isLoading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && filtered.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Nothing here yet.</Typography>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {filtered.map((a) => (
          <Paper key={a.id} variant="outlined" sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography fontWeight={700}>
                  {a.patientName ?? "Patient"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatSlotStart(a.slotStart, a.timeZone)}
                </Typography>
                {a.patientNote && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    "{a.patientNote}"
                  </Typography>
                )}
                <DoctorAppointmentFiles appointment={a} />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <AppointmentStatusBadge status={a.status} />
                {canJoinCall(a) && (
                  <Button
                    component={RouterLink}
                    to={`/doctor/consult/${a.id}`}
                    variant="contained"
                    size="small"
                  >
                    Open consult
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
